export const USER_ROLE = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  HEAD_PARTNER_MANAGER: 'head_partner_manager',
  PARTNER_MANAGER: 'partner_manager',
  NETWORK_CURATOR: 'network_curator',
  HEAD_CHAT_MANAGER: 'head_chat_manager',
  CHAT_MANAGER: 'chat_manager',
  USER: 'user',
} as const

export type UserRoleKey = typeof USER_ROLE[keyof typeof USER_ROLE]

export const PERMISSION = {
  ADMIN_HOME_VIEW: 'admin.home.view',
  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_VIEW_FULL: 'dashboard.view.full',
  DASHBOARD_VIEW_CHAT: 'dashboard.view.chat',
  DASHBOARD_VIEW_BUSINESS: 'dashboard.view.business',
  DASHBOARD_VIEW_FINANCE: 'dashboard.view.finance',
  CHATS_VIEW: 'chats.view',
  CHATS_REPLY: 'chats.reply',
  CHATS_START_CONVERSATION: 'chats.start_conversation',
  CHATS_SCHEDULE_MESSAGES: 'chats.schedule_messages',
  CHATS_EDIT_SCHEDULED_MESSAGES: 'chats.edit_scheduled_messages',
  CHATS_CANCEL_SCHEDULED_MESSAGES: 'chats.cancel_scheduled_messages',
  CHATS_ARCHIVE: 'chats.archive',
  CHATS_STATUS_MANAGE: 'chats.status.manage',
  CHATS_BLACKLIST: 'chats.blacklist',
  CHATS_CONTACT_PANEL_MANAGE: 'chats.contact_panel.manage',
  CHATS_NOTES_MANAGE: 'chats.notes.manage',
  CHATS_REQUISITES_VIEW: 'chats.requisites.view',
  CHATS_REQUISITES_MANAGE: 'chats.requisites.manage',
  CHATS_CLIENTIZE: 'chats.clientize',
  CHATS_LINK_PROFILE_MANAGE: 'chats.link_profile.manage',
  CHATS_OFFER_STATUSES_VIEW: 'chats.offer_statuses.view',
  CHATS_INTERNAL_EVENTS_VIEW: 'chats.internal_events.view',
  CHATS_MEDIA_VIEW: 'chats.media.view',
  CHATS_MEDIA_SEND: 'chats.media.send',
  CHATS_PRESENCE_VIEW: 'chats.presence.view',
  CHATS_SUPERVISE: 'chats.supervise',
  MESSAGE_CAMPAIGNS_VIEW: 'message_campaigns.view',
  MESSAGE_CAMPAIGNS_MANAGE: 'message_campaigns.manage',
  TEMPLATES_USE: 'templates.use',
  TEMPLATES_USE_ADMIN: 'templates.use.admin',
  TEMPLATES_MANAGE: 'templates.manage',
  PARTNERS_VIEW: 'partners.view',
  PARTNERS_MANAGE: 'partners.manage',
  PARTNERS_SUPERVISE: 'partners.supervise',
  REF_COMMISSIONS_VIEW: 'ref_commissions.view',
  REF_PAYOUTS_VIEW: 'ref_payouts.view',
  REF_PAYOUTS_MANAGE: 'ref_payouts.manage',
  TRACKING_VIEW: 'tracking.view',
  CLIENTS_VIEW: 'clients.view',
  CLIENTS_MANAGE: 'clients.manage',
  CLIENT_CONTEXT_VIEW: 'client_context.view',
  CLIENT_CONTEXT_MANAGE: 'client_context.manage',
  CLIENT_ASSIGNMENT_VIEW: 'client_assignment.view',
  CLIENT_ASSIGNMENT_MANAGE: 'client_assignment.manage',
  FINANCE_VIEW: 'finance.view',
  FINANCE_MANAGE: 'finance.manage',
  MATRIX_VIEW: 'matrix.view',
  MATRIX_MANAGE: 'matrix.manage',
  CONTACT_IDENTITIES_VIEW: 'contact.identities.view',
  CONTACT_IDENTITIES_MANAGE: 'contact.identities.manage',
  CONTACTS_VIEW: 'contacts.view',
  CONTACTS_MANAGE: 'contacts.manage',
  PERSONS_VIEW: 'persons.view',
  PERSONS_ATTACH: 'persons.attach',
  PERSONS_MERGE: 'persons.merge',
  ATTRIBUTION_VIEW: 'attribution.view',
  ATTRIBUTION_MANAGE: 'attribution.manage',
  PARTNERS_CREATE_FROM_CLIENT: 'partners.create_from_client',
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_MANAGE: 'settings.manage',
  USERS_VIEW: 'users.view',
  USERS_MANAGE: 'users.manage',
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',
  HORIZON_VIEW: 'horizon.view',
  SYSTEM_MANAGE: 'system.manage',
  NOTIFICATIONS_CHAT_RECEIVE: 'notifications.chat.receive',
  NOTIFICATIONS_CONVERSIONS_RECEIVE: 'notifications.conversions.receive',
  NOTIFICATIONS_CONVERSIONS_PAYOUT_VIEW: 'notifications.conversions.payout.view',
  NOTIFICATIONS_CONVERSIONS_ACCOUNT_VIEW: 'notifications.conversions.account.view',
  NOTIFICATIONS_CONVERSIONS_PARTNER_VIEW: 'notifications.conversions.partner.view',
} as const

export type PermissionKey = typeof PERMISSION[keyof typeof PERMISSION]

export const TEMPLATE_VISIBILITY = {
  ALL: 'all',
  ADMIN: 'admin',
  INTERNAL_STAFF: 'internal_staff',
  NETWORK_CURATORS: 'network_curators',
} as const

export type TemplateVisibilityKey = typeof TEMPLATE_VISIBILITY[keyof typeof TEMPLATE_VISIBILITY]

export type AuthAccess = {
  isAdmin: boolean
  isLegacyManager: boolean
  isManagerLike: boolean
  isInternalStaff: boolean
  isChatRole: boolean
  isPartnerRole: boolean
  isNetworkCurator: boolean
  canAccessAdminHome: boolean
  canAccessAdminPanel: boolean
  canAccessDashboard: boolean
  canViewFullDashboard: boolean
  canViewBusinessDashboard: boolean
  canViewFinanceDashboard: boolean
  canViewChatDashboard: boolean
  canAccessPartnerWorkspace: boolean
  canAccessPartnerSupervisorTools: boolean
  canViewRefCommissions: boolean
  canViewRefPayouts: boolean
  canManageRefPayouts: boolean
  canAccessChatWorkspace: boolean
  canStartConversation: boolean
  canAccessChatSupervisorTools: boolean
  canViewMessageCampaigns: boolean
  canManageMessageCampaigns: boolean
  canUseTemplateBrowser: boolean
  canUseAdminTemplates: boolean
  canUseChatActionTemplates: boolean
  canArchiveChats: boolean
  canManageChatStatus: boolean
  canManageChatBlacklist: boolean
  canManageChatContactPanel: boolean
  canManageChatNotes: boolean
  canViewChatRequisites: boolean
  canManageChatRequisites: boolean
  canClientizeFromChat: boolean
  canManageChatLinkProfile: boolean
  canViewChatOfferStatuses: boolean
  canManageChatContacts: boolean
  canViewContactRecords: boolean
  canManageContactRecords: boolean
  canManagePartnerRelations: boolean
  canManageClientRecords: boolean
  canViewClientRecords: boolean
  canViewClientContext: boolean
  canManageClientContext: boolean
  canViewClientAssignment: boolean
  canManageClientAssignment: boolean
  canViewPersons: boolean
  canAttachPersons: boolean
  canMergePersons: boolean
  canViewAttribution: boolean
  canManageAttribution: boolean
  canCreatePartnerFromClient: boolean
  canManageClientFinance: boolean
  canViewClientFinance: boolean
  canAccessTrackingAnalytics: boolean
  canAccessClientOfferMatrix: boolean
  canManageClientOfferMatrix: boolean
  canManageTemplates: boolean
  canUseInternalChatTools: boolean
  canUsePresence: boolean
  canViewContactIdentities: boolean
  canManageContactIdentities: boolean
  canViewSettings: boolean
  canManageSettings: boolean
  canViewUsers: boolean
  canManageUsers: boolean
  canViewRoles: boolean
  canManageRoles: boolean
  canManageSystem: boolean
  canViewHorizon: boolean
}

export type AuthPermissionMap = Record<string, boolean>

const ACCESS_DEFAULTS: AuthAccess = {
  isAdmin: false,
  isLegacyManager: false,
  isManagerLike: false,
  isInternalStaff: false,
  isChatRole: false,
  isPartnerRole: false,
  isNetworkCurator: false,
  canAccessAdminHome: false,
  canAccessAdminPanel: false,
  canAccessDashboard: false,
  canViewFullDashboard: false,
  canViewBusinessDashboard: false,
  canViewFinanceDashboard: false,
  canViewChatDashboard: false,
  canAccessPartnerWorkspace: false,
  canAccessPartnerSupervisorTools: false,
  canViewRefCommissions: false,
  canViewRefPayouts: false,
  canManageRefPayouts: false,
  canAccessChatWorkspace: false,
  canStartConversation: false,
  canAccessChatSupervisorTools: false,
  canViewMessageCampaigns: false,
  canManageMessageCampaigns: false,
  canUseTemplateBrowser: false,
  canUseAdminTemplates: false,
  canUseChatActionTemplates: false,
  canArchiveChats: false,
  canManageChatStatus: false,
  canManageChatBlacklist: false,
  canManageChatContactPanel: false,
  canManageChatNotes: false,
  canViewChatRequisites: false,
  canManageChatRequisites: false,
  canClientizeFromChat: false,
  canManageChatLinkProfile: false,
  canViewChatOfferStatuses: false,
  canManageChatContacts: false,
  canViewContactRecords: false,
  canManageContactRecords: false,
  canManagePartnerRelations: false,
  canManageClientRecords: false,
  canViewClientRecords: false,
  canViewClientContext: false,
  canManageClientContext: false,
  canViewClientAssignment: false,
  canManageClientAssignment: false,
  canViewPersons: false,
  canAttachPersons: false,
  canMergePersons: false,
  canViewAttribution: false,
  canManageAttribution: false,
  canCreatePartnerFromClient: false,
  canManageClientFinance: false,
  canViewClientFinance: false,
  canAccessTrackingAnalytics: false,
  canAccessClientOfferMatrix: false,
  canManageClientOfferMatrix: false,
  canManageTemplates: false,
  canUseInternalChatTools: false,
  canUsePresence: false,
  canViewContactIdentities: false,
  canManageContactIdentities: false,
  canViewSettings: false,
  canManageSettings: false,
  canViewUsers: false,
  canManageUsers: false,
  canViewRoles: false,
  canManageRoles: false,
  canManageSystem: false,
  canViewHorizon: false,
}

const ROLE_LABELS: Record<string, string> = {
  [USER_ROLE.ADMIN]: 'Администратор',
  [USER_ROLE.MANAGER]: 'Менеджер',
  [USER_ROLE.HEAD_PARTNER_MANAGER]: 'Руководитель партнёрского направления',
  [USER_ROLE.PARTNER_MANAGER]: 'Партнёрский менеджер',
  [USER_ROLE.NETWORK_CURATOR]: 'Куратор сети',
  [USER_ROLE.HEAD_CHAT_MANAGER]: 'Руководитель чат-направления',
  [USER_ROLE.CHAT_MANAGER]: 'Чат-менеджер',
  [USER_ROLE.USER]: 'Пользователь',
}

const ROLE_BADGES: Record<string, string> = {
  [USER_ROLE.ADMIN]: 'AD',
  [USER_ROLE.MANAGER]: 'MA',
  [USER_ROLE.HEAD_PARTNER_MANAGER]: 'HP',
  [USER_ROLE.PARTNER_MANAGER]: 'PM',
  [USER_ROLE.NETWORK_CURATOR]: 'NC',
  [USER_ROLE.HEAD_CHAT_MANAGER]: 'HC',
  [USER_ROLE.CHAT_MANAGER]: 'CM',
  [USER_ROLE.USER]: 'US',
}

const ROLE_STYLES: Record<string, string> = {
  [USER_ROLE.ADMIN]: 'bg-indigo-600 text-white',
  [USER_ROLE.MANAGER]: 'bg-emerald-600 text-white',
  [USER_ROLE.HEAD_PARTNER_MANAGER]: 'bg-amber-600 text-white',
  [USER_ROLE.PARTNER_MANAGER]: 'bg-orange-600 text-white',
  [USER_ROLE.NETWORK_CURATOR]: 'bg-emerald-700 text-white',
  [USER_ROLE.HEAD_CHAT_MANAGER]: 'bg-sky-600 text-white',
  [USER_ROLE.CHAT_MANAGER]: 'bg-cyan-600 text-white',
  [USER_ROLE.USER]: 'bg-slate-600 text-white',
}

const TEMPLATE_VISIBILITY_LABELS: Record<string, string> = {
  [TEMPLATE_VISIBILITY.ALL]: 'Всем',
  [TEMPLATE_VISIBILITY.ADMIN]: 'Только администраторам',
  [TEMPLATE_VISIBILITY.INTERNAL_STAFF]: 'Внутренней команде',
  [TEMPLATE_VISIBILITY.NETWORK_CURATORS]: 'Кураторам сети',
}

export const TEMPLATE_VISIBILITY_OPTIONS: Array<{ value: TemplateVisibilityKey; label: string }> = [
  { value: TEMPLATE_VISIBILITY.ALL, label: TEMPLATE_VISIBILITY_LABELS[TEMPLATE_VISIBILITY.ALL] },
  { value: TEMPLATE_VISIBILITY.ADMIN, label: TEMPLATE_VISIBILITY_LABELS[TEMPLATE_VISIBILITY.ADMIN] },
  { value: TEMPLATE_VISIBILITY.INTERNAL_STAFF, label: TEMPLATE_VISIBILITY_LABELS[TEMPLATE_VISIBILITY.INTERNAL_STAFF] },
  { value: TEMPLATE_VISIBILITY.NETWORK_CURATORS, label: TEMPLATE_VISIBILITY_LABELS[TEMPLATE_VISIBILITY.NETWORK_CURATORS] },
]

export function normalizeRole(role: unknown): string {
  return String(role ?? '').trim().toLowerCase()
}

export function getAuthAccess(pageProps: any): AuthAccess {
  const access = pageProps?.auth?.access ?? {}

  return {
    isAdmin: Boolean(access.is_admin),
    isLegacyManager: Boolean(access.is_legacy_manager),
    isManagerLike: Boolean(access.is_manager_like),
    isInternalStaff: Boolean(access.is_internal_staff),
    isChatRole: Boolean(access.is_chat_role),
    isPartnerRole: Boolean(access.is_partner_role),
    isNetworkCurator: Boolean(access.is_network_curator),
    canAccessAdminHome: Boolean(access.can_access_admin_home),
    canAccessAdminPanel: Boolean(access.can_access_admin_panel),
    canAccessDashboard: Boolean(access.can_access_dashboard),
    canViewFullDashboard: Boolean(access.can_view_full_dashboard),
    canViewBusinessDashboard: Boolean(access.can_view_business_dashboard),
    canViewFinanceDashboard: Boolean(access.can_view_finance_dashboard),
    canViewChatDashboard: Boolean(access.can_view_chat_dashboard),
    canAccessPartnerWorkspace: Boolean(access.can_access_partner_workspace),
    canAccessPartnerSupervisorTools: Boolean(access.can_access_partner_supervisor_tools),
    canViewRefCommissions: Boolean(access.can_view_ref_commissions),
    canViewRefPayouts: Boolean(access.can_view_ref_payouts),
    canManageRefPayouts: Boolean(access.can_manage_ref_payouts),
    canAccessChatWorkspace: Boolean(access.can_access_chat_workspace),
    canStartConversation: Boolean(access.can_start_conversation),
    canAccessChatSupervisorTools: Boolean(access.can_access_chat_supervisor_tools),
    canViewMessageCampaigns: Boolean(access.can_view_message_campaigns),
    canManageMessageCampaigns: Boolean(access.can_manage_message_campaigns),
    canUseTemplateBrowser: Boolean(access.can_use_template_browser),
    canUseAdminTemplates: Boolean(access.can_use_admin_templates),
    canUseChatActionTemplates: Boolean(access.can_use_chat_action_templates),
    canArchiveChats: Boolean(access.can_archive_chats),
    canManageChatStatus: Boolean(access.can_manage_chat_status),
    canManageChatBlacklist: Boolean(access.can_manage_chat_blacklist),
    canManageChatContactPanel: Boolean(access.can_manage_chat_contact_panel),
    canManageChatNotes: Boolean(access.can_manage_chat_notes),
    canViewChatRequisites: Boolean(access.can_view_chat_requisites),
    canManageChatRequisites: Boolean(access.can_manage_chat_requisites),
    canClientizeFromChat: Boolean(access.can_clientize_from_chat),
    canManageChatLinkProfile: Boolean(access.can_manage_chat_link_profile),
    canViewChatOfferStatuses: Boolean(access.can_view_chat_offer_statuses),
    canManageChatContacts: Boolean(access.can_manage_chat_contacts),
    canViewContactRecords: Boolean(access.can_view_contact_records),
    canManageContactRecords: Boolean(access.can_manage_contact_records),
    canManagePartnerRelations: Boolean(access.can_manage_partner_relations),
    canManageClientRecords: Boolean(access.can_manage_client_records),
    canViewClientRecords: Boolean(access.can_view_client_records),
    canViewClientContext: Boolean(access.can_view_client_context),
    canManageClientContext: Boolean(access.can_manage_client_context),
    canViewClientAssignment: Boolean(access.can_view_client_assignment),
    canManageClientAssignment: Boolean(access.can_manage_client_assignment),
    canViewPersons: Boolean(access.can_view_persons),
    canAttachPersons: Boolean(access.can_attach_persons),
    canMergePersons: Boolean(access.can_merge_persons),
    canViewAttribution: Boolean(access.can_view_attribution),
    canManageAttribution: Boolean(access.can_manage_attribution),
    canCreatePartnerFromClient: Boolean(access.can_create_partner_from_client),
    canManageClientFinance: Boolean(access.can_manage_client_finance),
    canViewClientFinance: Boolean(access.can_view_client_finance),
    canAccessTrackingAnalytics: Boolean(access.can_access_tracking_analytics),
    canAccessClientOfferMatrix: Boolean(access.can_access_client_offer_matrix),
    canManageClientOfferMatrix: Boolean(access.can_manage_client_offer_matrix),
    canManageTemplates: Boolean(access.can_manage_templates),
    canUseInternalChatTools: Boolean(access.can_use_internal_chat_tools),
    canUsePresence: Boolean(access.can_use_presence),
    canViewContactIdentities: Boolean(access.can_view_contact_identities),
    canManageContactIdentities: Boolean(access.can_manage_contact_identities),
    canViewSettings: Boolean(access.can_view_settings),
    canManageSettings: Boolean(access.can_manage_settings),
    canViewUsers: Boolean(access.can_view_users),
    canManageUsers: Boolean(access.can_manage_users),
    canViewRoles: Boolean(access.can_view_roles),
    canManageRoles: Boolean(access.can_manage_roles),
    canManageSystem: Boolean(access.can_manage_system),
    canViewHorizon: Boolean(access.can_view_horizon),
  }
}

export function getAuthAccessOrDefaults(pageProps: any): AuthAccess {
  return {
    ...ACCESS_DEFAULTS,
    ...getAuthAccess(pageProps),
  }
}

export function getAuthPermissionMap(pageProps: any): AuthPermissionMap {
  const raw = Array.isArray(pageProps?.auth?.permissions) ? pageProps.auth.permissions : []
  const map: AuthPermissionMap = {}

  for (const permission of raw) {
    const normalized = String(permission ?? '').trim().toLowerCase()
    if (normalized) {
      map[normalized] = true
    }
  }

  return map
}

export function hasAuthPermission(pageProps: any, permission: PermissionKey | string): boolean {
  const normalized = String(permission ?? '').trim().toLowerCase()
  if (!normalized) return false

  return Boolean(getAuthPermissionMap(pageProps)[normalized])
}

export function roleLabel(role: unknown): string {
  const normalized = normalizeRole(role)
  if (ROLE_LABELS[normalized]) {
    return ROLE_LABELS[normalized]
  }

  if (!normalized) {
    return ROLE_LABELS[USER_ROLE.USER]
  }

  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ')
}

export function roleBadge(role: unknown, name: string): string {
  const normalized = normalizeRole(role)
  if (ROLE_BADGES[normalized]) return ROLE_BADGES[normalized]

  const trimmed = String(name ?? '').trim()
  if (!trimmed) return '??'
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0].slice(0, 1) + parts[1].slice(0, 1)).toUpperCase()
  }
  return trimmed.slice(0, 2).toUpperCase()
}

export function roleFallbackStyle(role: unknown): string {
  const normalized = normalizeRole(role)
  return ROLE_STYLES[normalized] || ROLE_STYLES[USER_ROLE.USER]
}

export function templateVisibilityLabel(value: unknown): string {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === 'manager') {
    return TEMPLATE_VISIBILITY_LABELS[TEMPLATE_VISIBILITY.INTERNAL_STAFF]
  }
  return TEMPLATE_VISIBILITY_LABELS[normalized] || normalized
}
